// <copyright file="factory-tracking-reference-dialog.ts" company="SoftTech Ltd">
// Copyright (c) SoftTech Ltd. All rights reserved.
// </copyright>

import { buttonsSaveCancel } from '../../../webmodule-common/other/ui/modal-confirmation';
import { DataBinding } from '../../../webmodule-common/other/ui/databinding/databinding';
import { DataTracker, FieldType } from '../../../webmodule-common/other/ui/databinding/data-tracker';
import { FormInputAssistant } from '../../../webmodule-common/other/ui/templateresult/form-input-assistant';
import { html, type TemplateResult } from 'lit';
import { LangFabricationJobActions } from '../../../localization/scripts/fabrication-jobs/views/fabrication-job-actions-lang';
import { LitBaseModal } from '../../../webmodule-common/other/ui/modal/modal-factory-lit';

/**
 * One-field modal for the fabricator-internal tracking reference. An empty
 * value is a legitimate result - it clears the reference server-side.
 */
export class FactoryTrackingReferenceDialog extends LitBaseModal {
  public modalResult = false;
  private data: { reference: string };
  private dataTracker: DataTracker;
  private forms: FormInputAssistant;

  constructor(current: string) {
    super();
    this.data = { reference: current };
    const dataBinding = new DataBinding(
      this.ui,
      undefined,
      (input: string, internalId: string) => `factorytracking-${input}-${internalId}`
    );
    this.dataTracker = new DataTracker(dataBinding);
    this.dataTracker.addObjectBinding(() => this.data, 'reference', 'reference', FieldType.string, false);
    this.forms = new FormInputAssistant(this.dataTracker, false);
  }

  public static async Execute(current: string): Promise<string | null> {
    const dialog = new FactoryTrackingReferenceDialog(current);
    await dialog.showModal();
    return dialog.modalResult ? dialog.data.reference : null;
  }

  get isFooterVisible(): boolean {
    return true;
  }

  title(): string | TemplateResult {
    return LangFabricationJobActions.trackingReferenceDialogTitle();
  }

  bodyTemplate(): TemplateResult {
    return html`
      <form class="form-one-col">
        <div class="row">
          <div class="form-column">
            ${this.forms.text('reference', LangFabricationJobActions.trackingReferenceLabel(), 100)}
          </div>
        </div>
      </form>
    `;
  }

  footerTemplate(): TemplateResult | null | undefined {
    const buttons = buttonsSaveCancel();
    return this.createConfirmCancelButtons(buttons.ok, () => this.saveAndClose(), buttons.cancel);
  }

  private async saveAndClose() {
    this.dataTracker.applyChangeToValue();
    this.data.reference = this.data.reference?.trim() ?? '';
    this.modalResult = true;
    await this.hideModal();
  }
}
